/**
 * ThemeProvider — aplica e persiste preferências de UI (tema + accent + locale).
 *
 * Modos de tema:
 *   • light    — sempre claro
 *   • dark     — sempre escuro
 *   • system   — segue prefers-color-scheme do SO
 *   • auto     — alterna pelo horário do dia (6h-18h = claro)
 *
 * Locale (i18n):
 *   • pt | en | es
 *   • setLocale chama i18n.changeLanguage + persiste localStorage + PATCH /me
 *   • i18n.ts já tem detector custom que lê do mesmo localStorage no boot
 *
 * Persistência:
 *   • localStorage (instantâneo, funciona em qualquer rota)
 *   • PATCH /me/preferences em background (silently fail em rotas públicas)
 *   • Layout chama syncFromServer() depois que /me carrega
 *
 * Reatividade:
 *   • mode === system → escuta prefers-color-scheme
 *   • mode === auto   → recheck a cada minuto
 *   • storage event   → sincroniza entre abas
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from "react";
import { api } from "@/lib/api";
import {
  ACCENT_PALETTE,
  DEFAULT_PREFERENCES,
  isValidAccent,
  resolveTheme,
  type LocaleCode,
  type ThemeMode,
  type UserPreferences,
} from "@/lib/preferences";
import i18n from "@/lib/i18n";

const LS_KEY = "miller.preferences.v1";
const SUPPORTED_LOCALES: readonly LocaleCode[] = ["pt", "en", "es"];

interface ThemeContextValue {
  theme:           ThemeMode;
  accent:          string;
  locale:          LocaleCode;
  resolvedTheme:   "light" | "dark";
  setTheme:        (t: ThemeMode)  => void;
  setAccent:       (hex: string)   => void;
  setLocale:       (l: LocaleCode) => void;
  syncFromServer:  (prefs?: UserPreferences | null) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme deve ser usado dentro de <ThemeProvider>");
  return ctx;
}

// ── helpers ──────────────────────────────────────────────────────────────────

function readLocal(): UserPreferences {
  if (typeof window === "undefined") return {};
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeLocal(prefs: UserPreferences) {
  if (typeof window === "undefined") return;
  try { localStorage.setItem(LS_KEY, JSON.stringify(prefs)); } catch {}
}

function applyThemeToDOM(mode: ThemeMode): "light" | "dark" {
  const resolved = resolveTheme(mode);
  if (typeof document !== "undefined") {
    document.documentElement.classList.toggle("dark", resolved === "dark");
    document.documentElement.dataset.theme = resolved;
    document.documentElement.dataset.themeMode = mode;
    document.documentElement.style.colorScheme = resolved;
  }
  return resolved;
}

function applyAccentToDOM(hex: string) {
  if (typeof document === "undefined") return;
  if (!isValidAccent(hex)) return;
  const root = document.documentElement;
  root.style.setProperty("--accent", hex);
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  root.style.setProperty("--accent-rgb",  `${r} ${g} ${b}`);
  root.style.setProperty("--accent-soft", `rgba(${r}, ${g}, ${b}, 0.18)`);
}

function applyLocaleToDOM(loc: LocaleCode) {
  if (typeof document === "undefined") return;
  document.documentElement.lang = loc;
}

function isValidLocale(s: unknown): s is LocaleCode {
  return typeof s === "string" && (SUPPORTED_LOCALES as readonly string[]).includes(s);
}

// ── provider ─────────────────────────────────────────────────────────────────

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState]   = useState<ThemeMode>(DEFAULT_PREFERENCES.theme);
  const [accent, setAccentState] = useState<string>(DEFAULT_PREFERENCES.accent);
  const [locale, setLocaleState] = useState<LocaleCode>(DEFAULT_PREFERENCES.locale);
  const [resolvedTheme, setResolvedTheme] = useState<"light" | "dark">("light");

  // Hidratação client-side
  useEffect(() => {
    const local = readLocal();
    if (local.theme)  setThemeState(local.theme);
    if (local.accent && isValidAccent(local.accent)) setAccentState(local.accent);
    // i18next já leu do localStorage via detector custom; aqui só sincroniza o state
    const detected = (i18n.language || "pt").split("-")[0];
    if (isValidLocale(detected)) setLocaleState(detected);
  }, []);

  // Aplicar tema, accent e locale no DOM
  useEffect(() => { setResolvedTheme(applyThemeToDOM(theme)); }, [theme]);
  useEffect(() => { applyAccentToDOM(accent); }, [accent]);
  useEffect(() => { applyLocaleToDOM(locale); }, [locale]);

  // Reagir a mudança do SO quando em "system"
  useEffect(() => {
    if (theme !== "system") return;
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => setResolvedTheme(applyThemeToDOM("system"));
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, [theme]);

  // Modo "auto": recheck a cada minuto
  useEffect(() => {
    if (theme !== "auto") return;
    if (typeof window === "undefined") return;
    const tick = () => setResolvedTheme(applyThemeToDOM("auto"));
    const interval = window.setInterval(tick, 60_000);
    const onFocus = () => tick();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [theme]);

  // Sincronizar entre abas
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onStorage = (e: StorageEvent) => {
      if (e.key !== LS_KEY || !e.newValue) return;
      try {
        const next = JSON.parse(e.newValue) as UserPreferences;
        if (next.theme  && next.theme  !== theme)  setThemeState(next.theme);
        if (next.accent && next.accent !== accent && isValidAccent(next.accent)) {
          setAccentState(next.accent);
        }
        if (next.locale && next.locale !== locale && isValidLocale(next.locale)) {
          setLocaleState(next.locale);
          i18n.changeLanguage(next.locale);
        }
      } catch {}
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [theme, accent, locale]);

  const stateRef = useRef({ theme, accent, locale });
  stateRef.current = { theme, accent, locale };

  const setTheme = useCallback((t: ThemeMode) => {
    setThemeState(t);
    writeLocal({ ...readLocal(), ...stateRef.current, theme: t });
    api.patch("/me/preferences", { theme: t }).catch(() => {});
  }, []);

  const setAccent = useCallback((hex: string) => {
    if (!isValidAccent(hex)) return;
    setAccentState(hex);
    writeLocal({ ...readLocal(), ...stateRef.current, accent: hex });
    api.patch("/me/preferences", { accent: hex }).catch(() => {});
  }, []);

  const setLocale = useCallback((l: LocaleCode) => {
    if (!isValidLocale(l)) return;
    setLocaleState(l);
    i18n.changeLanguage(l);
    writeLocal({ ...readLocal(), ...stateRef.current, locale: l });
    api.patch("/me/preferences", { locale: l }).catch(() => {});
  }, []);

  const syncFromServer = useCallback((prefs?: UserPreferences | null) => {
    if (!prefs) return;
    const updates: UserPreferences = {};
    if (prefs.theme && prefs.theme !== stateRef.current.theme) {
      setThemeState(prefs.theme);
      updates.theme = prefs.theme;
    }
    if (prefs.accent && isValidAccent(prefs.accent) && prefs.accent !== stateRef.current.accent) {
      setAccentState(prefs.accent);
      updates.accent = prefs.accent;
    }
    if (prefs.locale && isValidLocale(prefs.locale) && prefs.locale !== stateRef.current.locale) {
      setLocaleState(prefs.locale);
      i18n.changeLanguage(prefs.locale);
      updates.locale = prefs.locale;
    }
    if (Object.keys(updates).length > 0) {
      writeLocal({ ...readLocal(), ...updates });
    }
  }, []);

  const value = useMemo<ThemeContextValue>(() => ({
    theme, accent, locale, resolvedTheme,
    setTheme, setAccent, setLocale, syncFromServer,
  }), [theme, accent, locale, resolvedTheme, setTheme, setAccent, setLocale, syncFromServer]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export { ACCENT_PALETTE };
